import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  GraduationCap,
  CalendarDays,
  Search,
  Filter,
  Layers
} from 'lucide-react';

interface Employee {
  id: string;
  full_name: string;
}

// Donnée brute de la DB
interface TrainingDayRaw {
  id: string;
  day: string;
  description: string;
  created_at: string;
  employee_id: string;
  employee: { full_name: string };
}

// Session regroupée pour l'affichage
interface TrainingSession {
  uniqueKey: string; // employeeId + createdAt
  employeeName: string;
  description: string;
  dates: string[]; // Liste des jours (YYYY-MM-DD)
  ids: string[]; // Liste des IDs DB à supprimer
  createdAt: string;
}

export default function AdminTraining() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [loading, setLoading] = useState(true);

  // Formulaire Ajout
  const [selectedEmp, setSelectedEmp] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [desc, setDesc] = useState('');

  // Filtres Liste
  const [filterText, setFilterText] = useState('');
  const [filterMonth, setFilterMonth] = useState('');

  // --- 1. CHARGEMENT ET REGROUPEMENT ---
  const fetchData = useCallback(async () => {
    try {
      // A. Employés
      const { data: empData } = await supabase
        .from('employees')
        .select('id, full_name')
        .eq('is_active', true)
        .order('full_name');
      setEmployees(empData || []);

      // B. Jours de formation bruts
      const { data: rawData, error } = await supabase
        .from('training_days')
        .select('*, employee:employees(full_name)')
        .order('created_at', { ascending: false }); // Plus récent ajout en haut
      
      if (error) throw error;

      // C. Regroupement intelligent par "Batch" (Même employé + Même moment de création)
      const rawTrainings = rawData as unknown as TrainingDayRaw[];
      const groups: Record<string, TrainingSession> = {};

      rawTrainings.forEach(t => {
        // Clé unique pour grouper : ID Employé + Timestamp création (à la seconde près)
        // On découpe created_at pour éviter les micro-différences de ms si besoin, 
        // mais généralement le batch insert met le même timestamp exact.
        const batchKey = `${t.employee_id}_${t.created_at}`;

        if (!groups[batchKey]) {
          groups[batchKey] = {
            uniqueKey: batchKey,
            employeeName: t.employee?.full_name || 'Inconnu',
            description: t.description,
            createdAt: t.created_at,
            dates: [],
            ids: []
          };
        }
        groups[batchKey].dates.push(t.day);
        groups[batchKey].ids.push(t.id);
      });

      // Transformation en tableau + Tri des dates dans chaque session
      const groupedList = Object.values(groups).map(session => {
        session.dates.sort(); // Trier les jours chronologiquement (ex: 12, 13, 14)
        return session;
      });

      // Tri final par date de création (le dernier ajouté en haut)
      groupedList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setSessions(groupedList);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- 2. FILTRAGE VISUEL ---
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      const matchText = s.employeeName.toLowerCase().includes(filterText.toLowerCase()) || 
                        s.description?.toLowerCase().includes(filterText.toLowerCase());
      
      // Pour le mois, on regarde si au moins une date de la session correspond
      const matchDate = filterMonth 
        ? s.dates.some(d => d.startsWith(filterMonth))
        : true;

      return matchText && matchDate;
    });
  }, [sessions, filterText, filterMonth]);

  // --- 3. ACTIONS ---
  
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmp || !startDate || !endDate) { toast.warning("Champs requis manquants"); return; }
    if (new Date(startDate) > new Date(endDate)) { toast.warning("Date de fin antérieure au début"); return; }

    setLoading(true);
    try {
        const datesToInsert = [];
        const current = new Date(startDate);
        const end = new Date(endDate);

        while (current <= end) {
            datesToInsert.push({
                employee_id: selectedEmp,
                day: current.toISOString().split('T')[0],
                description: desc || 'Formation'
            });
            current.setDate(current.getDate() + 1);
        }

        const { error } = await supabase.from('training_days').insert(datesToInsert);

        if (error) {
            if (error.code === '23505') toast.warning("Conflit : Certains jours existent déjà pour cet employé.");
            else throw error;
        } else {
            setDesc('');
            setStartDate('');
            setEndDate('');
            fetchData(); // Recharger pour voir le nouveau groupe
        }
    } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l'ajout.");
    } finally {
        setLoading(false);
    }
  };

  const handleDeleteSession = async (ids: string[]) => {
    if (!confirm(`Supprimer cette session complète (${ids.length} jours) ?`)) return;
    setLoading(true);
    try {
        await supabase.from('training_days').delete().in('id', ids);
        fetchData();
    } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la suppression.");
    } finally {
        setLoading(false);
    }
  };

  // Helper affichage période propre
  const formatPeriod = (dates: string[]) => {
      if (dates.length === 0) return "-";
      // Dates sont déjà triées
      const start = new Date(dates[0]);
      const end = new Date(dates[dates.length - 1]);
      
      const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
      
      if (dates.length === 1) {
          return `Le ${start.toLocaleDateString('fr-FR', options)}`;
      }
      
      // Vérifier si les jours sont consécutifs (optionnel, pour l'affichage simple on prend juste min et max)
      return `Du ${start.toLocaleDateString('fr-FR', options)} au ${end.toLocaleDateString('fr-FR', options)}`;
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth:'1000px', margin:'0 auto', padding:'20px', paddingBottom:'80px' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer' }}>
            <ArrowLeft size={20} color="#64748b" />
        </button>
        <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Gestion des Formations</h1>
      </div>

      {/* FORMULAIRE */}
      <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '40px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
        <h3 style={{ marginTop: 0, display:'flex', alignItems:'center', gap:'10px', color:'#4338ca' }}>
          <div style={{ background:'#e0e7ff', padding:'8px', borderRadius:'8px' }}><GraduationCap size={24} /></div>
          Nouvelle Session
        </h3>
        <p style={{ color:'#64748b', fontSize:'0.9rem', marginBottom:'20px' }}>
          Sélectionnez une période. Tous les jours inclus seront ajoutés (7h, sans indemnités).
        </p>
        
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems:'flex-end' }}>
          <label style={{ flex: 1, minWidth: '250px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>
            Collaborateur
            <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)} style={{ width: '100%', padding: '12px', marginTop:'6px', borderRadius:'8px', border:'1px solid #cbd5e1', background:'white' }} required>
              <option value="">-- Sélectionner --</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
            </select>
          </label>

          <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
            <label style={{ width: '150px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>
                Date Début
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%', padding: '11px', marginTop:'6px', borderRadius:'8px', border:'1px solid #cbd5e1' }} required />
            </label>
            <label style={{ width: '150px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>
                Date Fin
                <input type="date" min={startDate} value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%', padding: '11px', marginTop:'6px', borderRadius:'8px', border:'1px solid #cbd5e1' }} required />
            </label>
          </div>

          <label style={{ flex: 2, minWidth: '250px', fontWeight:'600', color:'#475569', fontSize:'0.9rem' }}>
            Intitulé
            <input type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Ex: CACES, Habilitation..." style={{ width: '100%', padding: '11px', marginTop:'6px', borderRadius:'8px', border:'1px solid #cbd5e1' }} />
          </label>

          <button type="submit" disabled={loading} style={{ padding: '12px 24px', background: '#4338ca', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'wait' : 'pointer', fontWeight: '600', display:'flex', alignItems:'center', gap:'8px', height:'46px', boxShadow:'0 2px 4px rgba(67, 56, 202, 0.2)' }}>
            <Plus size={18} /> Ajouter
          </button>
        </form>
      </div>

      {/* --- HISTORIQUE GROUPÉ --- */}
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'20px', flexWrap:'wrap', gap:'15px' }}>
            <h2 style={{ fontSize:'1.2rem', color:'#1e293b', margin:0, display:'flex', alignItems:'center', gap:'10px' }}>
                <Layers size={22} className="text-gray-500" />
                Sessions Planifiées
            </h2>

            {/* BARRE DE FILTRES */}
            <div style={{ display:'flex', gap:'10px', background:'white', padding:'5px', borderRadius:'10px', border:'1px solid #e2e8f0' }}>
                <div style={{ position:'relative' }}>
                    <Search size={16} color="#94a3b8" style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)' }} />
                    <input 
                        type="text" 
                        placeholder="Rechercher..." 
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        style={{ padding:'8px 8px 8px 32px', border:'none', background:'transparent', outline:'none', fontSize:'0.9rem', width:'180px' }}
                    />
                </div>
                <div style={{ width:'1px', background:'#e2e8f0' }}></div>
                <div style={{ display:'flex', alignItems:'center', padding:'0 10px', gap:'8px' }}>
                    <Filter size={16} color="#94a3b8" />
                    <input 
                        type="month" 
                        value={filterMonth}
                        onChange={e => setFilterMonth(e.target.value)}
                        style={{ border:'none', background:'transparent', outline:'none', color:'#475569', cursor:'pointer', fontSize:'0.9rem' }}
                    />
                    {filterMonth && <button onClick={() => setFilterMonth('')} style={{border:'none', background:'transparent', color:'#ef4444', cursor:'pointer', fontWeight:'bold'}}>×</button>}
                </div>
            </div>
        </div>

        {/* LISTE DES SESSIONS */}
        {filteredSessions.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px', background:'white', borderRadius:'16px', border:'1px dashed #cbd5e1', color:'#94a3b8' }}>
                Aucune session trouvée.
            </div>
        ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'15px' }}>
                {filteredSessions.map((session) => {
                    const isPast = new Date(session.dates[session.dates.length-1]) < new Date(new Date().setHours(0,0,0,0));
                    
                    return (
                        <div key={session.uniqueKey} style={{ 
                            background: 'white', 
                            borderRadius:'12px', 
                            border: '1px solid #e2e8f0', 
                            padding:'20px', 
                            display:'flex', 
                            alignItems:'center', 
                            justifyContent:'space-between',
                            opacity: isPast ? 0.75 : 1,
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                        }}>
                            
                            {/* GAUCHE : INFO PRINCIPALE */}
                            <div style={{ display:'flex', gap:'20px', alignItems:'center' }}>
                                {/* Avatar */}
                                <div style={{ 
                                    width:'48px', height:'48px', borderRadius:'12px', 
                                    background: isPast ? '#f1f5f9' : '#e0e7ff', 
                                    color: isPast ? '#94a3b8' : '#4338ca',
                                    display:'flex', alignItems:'center', justifyContent:'center',
                                    fontWeight:'700', fontSize:'1.1rem'
                                }}>
                                    {session.employeeName.charAt(0)}
                                </div>

                                <div>
                                    <div style={{ fontWeight:'700', color:'#1e293b', fontSize:'1.05rem', marginBottom:'4px' }}>
                                        {session.employeeName}
                                    </div>
                                    <div style={{ display:'flex', alignItems:'center', gap:'10px', fontSize:'0.9rem', color:'#64748b' }}>
                                        <span style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                            <CalendarDays size={16} />
                                            {formatPeriod(session.dates)}
                                        </span>
                                        <span style={{ background: isPast ? '#f1f5f9' : '#ecfdf5', color: isPast ? '#64748b' : '#059669', padding:'2px 8px', borderRadius:'20px', fontSize:'0.75rem', fontWeight:'600' }}>
                                            {session.dates.length} jour{session.dates.length > 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* CENTRE : DESCRIPTION */}
                            <div style={{ flex: 1, padding:'0 30px' }}>
                                <div style={{ background:'#f8fafc', padding:'8px 12px', borderRadius:'8px', color:'#475569', fontSize:'0.9rem', border:'1px solid #f1f5f9', display:'inline-block' }}>
                                    {session.description}
                                </div>
                            </div>

                            {/* DROITE : ACTIONS */}
                            <div style={{ display:'flex', alignItems:'center', gap:'15px' }}>
                                <div style={{ textAlign:'right', fontSize:'0.75rem', color:'#94a3b8' }}>
                                    Ajouté le {new Date(session.createdAt).toLocaleDateString()}
                                </div>
                                <button 
                                    onClick={() => handleDeleteSession(session.ids)} 
                                    title="Supprimer toute la session" 
                                    style={{ 
                                        width:'36px', height:'36px', borderRadius:'8px', border:'1px solid #fee2e2', 
                                        background:'#fef2f2', color:'#ef4444', cursor:'pointer', 
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = 'white'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>

                        </div>
                    );
                })}
            </div>
        )}
      </div>
    </div>
  );
}